import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  ChevronLeft, Loader2, CheckCircle, Clock, AlertCircle,
  Home, User, PoundSterling, Building2, FileText, Calendar,
  Phone, Mail, MapPin, LogOut, Scale, ArrowRight
} from "lucide-react";
import { API } from "@/App";

const getPortalAuth = () => {
  const token = localStorage.getItem("portal_token");
  const client = JSON.parse(localStorage.getItem("portal_client") || "{}");
  return { token, client };
};

const MILESTONE_CONFIG = [
  { key: "sale_agreed", label: "Sale Agreed", icon: CheckCircle, owner: "Estate Agent" },
  { key: "memorandum_issued", label: "Memorandum of Sale Issued", icon: FileText, owner: "Estate Agent" },
  { key: "aml_checks", label: "AML / Buyer Checks Completed", icon: User, owner: "Buyer" },
  { key: "solicitors_instructed", label: "Solicitors Instructed", icon: Scale, owner: "Buyer & Seller" },
  { key: "mortgage_submitted", label: "Mortgage Application Submitted", icon: Building2, owner: "Buyer" },
  { key: "mortgage_offer", label: "Mortgage Offer Received", icon: CheckCircle, owner: "Buyer" },
  { key: "survey_booked", label: "Survey Booked", icon: Calendar, owner: "Buyer" },
  { key: "survey_received", label: "Survey Received", icon: FileText, owner: "Buyer" },
  { key: "searches_contract_initiated", label: "Searches / Contract Initiated", icon: FileText, owner: "Solicitors" },
  { key: "searches_returned", label: "Searches Returned", icon: CheckCircle, owner: "Solicitors" },
  { key: "enquiries_raised", label: "Enquiries Raised", icon: FileText, owner: "Solicitors" },
  { key: "enquiries_satisfied", label: "Enquiries Satisfied", icon: CheckCircle, owner: "Solicitors" },
  { key: "exchange", label: "Exchange", icon: ArrowRight, owner: "All Parties" },
  { key: "completion", label: "Completion", icon: Home, owner: "All Parties" }
];

export const SellerProgressionPage = () => {
  const { caseId } = useParams();
  const navigate = useNavigate();
  const { token, client } = getPortalAuth();
  
  const [loading, setLoading] = useState(true);
  const [caseData, setCaseData] = useState(null);
  const [milestones, setMilestones] = useState({});
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    if (!token) {
      navigate("/portal/login");
      return;
    }
    fetchProgressionData();
  }, [token, caseId]);

  const fetchProgressionData = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.get(`${API}/portal/seller/progression/${caseId}`, { headers });
      setCaseData(response.data.case_data || response.data);
      setMilestones(response.data.milestones || {});
      
      // Calculate progress
      const completedCount = MILESTONE_CONFIG.filter(m => 
        response.data.milestones?.[m.key]?.status === 'completed'
      ).length;
      setProgress(Math.round((completedCount / MILESTONE_CONFIG.length) * 100));
    } catch (error) {
      console.error("Error fetching progression:", error);
      if (error.response?.status === 401 || error.response?.status === 403) {
        navigate("/portal/login");
      } else {
        toast.error("Failed to load progression data");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("portal_token");
    localStorage.removeItem("portal_client");
    navigate("/portal/login");
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return null;
    return new Date(dateStr).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-slate-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-100">
      {/* Header */}
      <header className="bg-black text-white sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <img 
                src="/goodacres-full-logo.jpg" 
                alt="Goodacres" 
                className="h-12 w-auto rounded"
              />
              <div className="border-l border-slate-600 pl-4">
                <h1 className="font-semibold text-lg">Sales Progression</h1>
                <p className="text-sm text-slate-400">{client?.name || 'Client'}</p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <Button 
                variant="ghost" 
                onClick={() => navigate("/portal/seller")}
                className="text-white hover:bg-slate-800"
              >
                <ChevronLeft className="w-4 h-4 mr-2" />
                Back to Portal
              </Button>
              <Button 
                variant="outline" 
                onClick={handleLogout}
                className="border-slate-600 text-white hover:bg-slate-800"
              >
                <LogOut className="w-4 h-4 mr-2" />
                Sign Out
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-8">
        {/* Property Header */}
        <Card className="mb-6 overflow-hidden">
          <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white p-6">
            <div className="flex items-start justify-between">
              <div>
                <div className="flex items-center gap-3 mb-2">
                  <Badge className="bg-white/20 text-white">SSTC</Badge>
                  <Badge className="bg-emerald-500 text-white">Sales Progression</Badge>
                </div>
                <h1 className="text-3xl font-bold">{caseData?.address_line1 || 'Property'}</h1>
                <p className="text-blue-100 text-lg">{caseData?.postcode}</p>
              </div>
              <div className="text-right">
                <p className="text-blue-100 text-sm">Sale Price</p>
                <p className="text-4xl font-bold">
                  £{(caseData?.offer_price || caseData?.asking_price || 0).toLocaleString()}
                </p>
                <div className="mt-3">
                  <p className="text-blue-100 text-sm mb-1">Progress</p>
                  <div className="flex items-center gap-3">
                    <Progress value={progress} className="w-32 h-2" />
                    <span className="font-bold">{progress}%</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Card>

        {/* Main Content Tabs */}
        <Tabs defaultValue="milestones">
          <TabsList className="mb-6">
            <TabsTrigger value="milestones">Milestones</TabsTrigger>
            <TabsTrigger value="parties">Parties</TabsTrigger>
            <TabsTrigger value="details">Details</TabsTrigger>
          </TabsList>

          {/* Milestones Tab */}
          <TabsContent value="milestones">
            <Card>
              <CardHeader>
                <CardTitle>Sales Progression Milestones</CardTitle>
                <CardDescription>Track the progress of this sale</CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                {MILESTONE_CONFIG.map((milestone, idx) => {
                  const data = milestones[milestone.key] || {};
                  const status = data.status || 'not_started';
                  const isCompleted = status === 'completed';
                  const isInProgress = status === 'in_progress';
                  const Icon = milestone.icon;
                  
                  return (
                    <div 
                      key={milestone.key}
                      className={`flex items-center gap-4 p-4 rounded-lg border transition-all ${
                        isCompleted ? "bg-green-50 border-green-200" :
                        isInProgress ? "bg-amber-50 border-amber-200" :
                        "bg-white border-slate-200"
                      }`}
                    >
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
                        isCompleted ? "bg-green-500 text-white" :
                        isInProgress ? "bg-amber-500 text-white" :
                        "bg-slate-100 text-slate-400"
                      }`}>
                        {isCompleted ? (
                          <CheckCircle className="w-5 h-5" />
                        ) : (
                          <span className="font-medium">{idx + 1}</span>
                        )}
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <Icon className={`w-4 h-4 ${
                            isCompleted ? "text-green-600" :
                            isInProgress ? "text-amber-600" :
                            "text-slate-400"
                          }`} />
                          <p className={`font-medium ${
                            isCompleted ? "text-green-700" :
                            isInProgress ? "text-amber-700" :
                            "text-slate-700"
                          }`}>
                            {milestone.label}
                          </p>
                        </div>
                        {data.completed_at && (
                          <p className="text-xs text-slate-500 mt-1">
                            Completed: {formatDate(data.completed_at)}
                          </p>
                        )}
                      </div>
                      <div className="text-right">
                        <Badge className={
                          isCompleted ? "bg-green-100 text-green-700" :
                          isInProgress ? "bg-amber-100 text-amber-700" :
                          "bg-slate-100 text-slate-500"
                        }>
                          {isCompleted ? "Completed" : isInProgress ? "In Progress" : "Not Started"}
                        </Badge>
                      </div>
                    </div>
                  );
                })}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Parties Tab */}
          <TabsContent value="parties">
            <Card>
              <CardHeader>
                <CardTitle>Parties Involved</CardTitle>
                <CardDescription>All parties involved in this sale</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid md:grid-cols-2 gap-6">
                  {/* Seller */}
                  <div className="p-5 bg-emerald-50 rounded-xl border-2 border-emerald-200">
                    <div className="flex items-center gap-2 mb-4">
                      <div className="w-10 h-10 bg-emerald-500 rounded-full flex items-center justify-center">
                        <User className="w-5 h-5 text-white" />
                      </div>
                      <div>
                        <p className="text-xs text-emerald-600 font-semibold uppercase">Seller</p>
                        <p className="font-bold text-slate-800">{caseData?.seller_name || caseData?.vendor_name || 'Not set'}</p>
                      </div>
                    </div>
                    {caseData?.seller_email && (
                      <p className="text-sm text-slate-600 flex items-center gap-2 mb-1">
                        <Mail className="w-4 h-4" /> {caseData.seller_email}
                      </p>
                    )}
                    {caseData?.seller_phone && (
                      <p className="text-sm text-slate-600 flex items-center gap-2">
                        <Phone className="w-4 h-4" /> {caseData.seller_phone}
                      </p>
                    )}
                  </div>

                  {/* Buyer */}
                  <div className="p-5 bg-blue-50 rounded-xl border-2 border-blue-200">
                    <div className="flex items-center gap-2 mb-4">
                      <div className="w-10 h-10 bg-blue-500 rounded-full flex items-center justify-center">
                        <User className="w-5 h-5 text-white" />
                      </div>
                      <div>
                        <p className="text-xs text-blue-600 font-semibold uppercase">Buyer</p>
                        <p className="font-bold text-slate-800">{caseData?.buyer_name || 'Not set'}</p>
                      </div>
                    </div>
                    {caseData?.buyer_email && (
                      <p className="text-sm text-slate-600 flex items-center gap-2 mb-1">
                        <Mail className="w-4 h-4" /> {caseData.buyer_email}
                      </p>
                    )}
                    {caseData?.buyer_phone && (
                      <p className="text-sm text-slate-600 flex items-center gap-2">
                        <Phone className="w-4 h-4" /> {caseData.buyer_phone}
                      </p>
                    )}
                  </div>

                  {/* Seller Solicitor */}
                  <div className={`p-5 rounded-xl border-2 ${
                    caseData?.seller_solicitor_firm 
                      ? "bg-amber-50 border-amber-200" 
                      : "bg-slate-50 border-slate-200 border-dashed"
                  }`}>
                    <div className="flex items-center gap-2 mb-4">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                        caseData?.seller_solicitor_firm ? "bg-amber-500" : "bg-slate-300"
                      }`}>
                        <Scale className="w-5 h-5 text-white" />
                      </div>
                      <div>
                        <p className={`text-xs font-semibold uppercase ${
                          caseData?.seller_solicitor_firm ? "text-amber-600" : "text-slate-500"
                        }`}>Seller Solicitor</p>
                        <p className="font-bold text-slate-800">
                          {caseData?.seller_solicitor_firm || 'Not yet appointed'}
                        </p>
                      </div>
                    </div>
                    {caseData?.seller_solicitor_name && (
                      <p className="text-sm text-slate-600 mb-1">{caseData.seller_solicitor_name}</p>
                    )}
                    {caseData?.seller_solicitor_email && (
                      <p className="text-sm text-slate-600 flex items-center gap-2 mb-1">
                        <Mail className="w-4 h-4" /> {caseData.seller_solicitor_email}
                      </p>
                    )}
                    {caseData?.seller_solicitor_phone && (
                      <p className="text-sm text-slate-600 flex items-center gap-2">
                        <Phone className="w-4 h-4" /> {caseData.seller_solicitor_phone}
                      </p>
                    )}
                  </div>

                  {/* Buyer Solicitor */}
                  <div className={`p-5 rounded-xl border-2 ${
                    caseData?.buyer_solicitor_firm 
                      ? "bg-purple-50 border-purple-200" 
                      : "bg-slate-50 border-slate-200 border-dashed"
                  }`}>
                    <div className="flex items-center gap-2 mb-4">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                        caseData?.buyer_solicitor_firm ? "bg-purple-500" : "bg-slate-300"
                      }`}>
                        <Scale className="w-5 h-5 text-white" />
                      </div>
                      <div>
                        <p className={`text-xs font-semibold uppercase ${
                          caseData?.buyer_solicitor_firm ? "text-purple-600" : "text-slate-500"
                        }`}>Buyer Solicitor</p>
                        <p className="font-bold text-slate-800">
                          {caseData?.buyer_solicitor_firm || 'Not yet appointed'}
                        </p>
                      </div>
                    </div>
                    {caseData?.buyer_solicitor_name && (
                      <p className="text-sm text-slate-600 mb-1">{caseData.buyer_solicitor_name}</p>
                    )}
                    {caseData?.buyer_solicitor_email && (
                      <p className="text-sm text-slate-600 flex items-center gap-2 mb-1">
                        <Mail className="w-4 h-4" /> {caseData.buyer_solicitor_email}
                      </p>
                    )}
                    {caseData?.buyer_solicitor_phone && (
                      <p className="text-sm text-slate-600 flex items-center gap-2">
                        <Phone className="w-4 h-4" /> {caseData.buyer_solicitor_phone}
                      </p>
                    )}
                  </div>
                </div>

                {/* Estate Agent */}
                <div className="mt-6 p-5 bg-slate-50 rounded-xl border border-slate-200">
                  <div className="flex items-center gap-2 mb-4">
                    <div className="w-10 h-10 bg-slate-700 rounded-full flex items-center justify-center">
                      <Building2 className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <p className="text-xs text-slate-500 font-semibold uppercase">Estate Agent</p>
                      <p className="font-bold text-slate-800">Goodacres Residential</p>
                    </div>
                  </div>
                  {caseData?.negotiator && (
                    <p className="text-sm text-slate-600 mb-1">Negotiator: {caseData.negotiator}</p>
                  )}
                  <p className="text-sm text-slate-600 flex items-center gap-2">
                    <Phone className="w-4 h-4" /> 01234 123456
                  </p>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Details Tab */}
          <TabsContent value="details">
            <Card>
              <CardHeader>
                <CardTitle>Sale Details</CardTitle>
                <CardDescription>Key information about this sale</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Key Dates */}
                <div>
                  <h4 className="font-semibold text-slate-700 mb-4 flex items-center gap-2">
                    <Calendar className="w-5 h-5" />
                    Key Dates
                  </h4>
                  <div className="grid md:grid-cols-3 gap-4">
                    <div className="p-4 bg-blue-50 rounded-lg text-center">
                      <p className="text-xs text-blue-600 font-semibold uppercase">Sale Agreed</p>
                      <p className="text-lg font-bold text-blue-700 mt-1">
                        {caseData?.sale_agreed_date ? formatDate(caseData.sale_agreed_date) : 'TBC'}
                      </p>
                    </div>
                    <div className="p-4 bg-amber-50 rounded-lg text-center">
                      <p className="text-xs text-amber-600 font-semibold uppercase">Target Exchange</p>
                      <p className="text-lg font-bold text-amber-700 mt-1">
                        {caseData?.target_exchange_date ? formatDate(caseData.target_exchange_date) : 'TBC'}
                      </p>
                    </div>
                    <div className="p-4 bg-emerald-50 rounded-lg text-center">
                      <p className="text-xs text-emerald-600 font-semibold uppercase">Target Completion</p>
                      <p className="text-lg font-bold text-emerald-700 mt-1">
                        {caseData?.completion_date ? formatDate(caseData.completion_date) : 'TBC'}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Property Details */}
                <div>
                  <h4 className="font-semibold text-slate-700 mb-4 flex items-center gap-2">
                    <Home className="w-5 h-5" />
                    Property Details
                  </h4>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="p-4 bg-slate-50 rounded-lg">
                      <p className="text-xs text-slate-500 uppercase mb-1">Address</p>
                      <p className="font-medium">{caseData?.address_line1}</p>
                      {caseData?.address_line2 && <p>{caseData.address_line2}</p>}
                      <p>{caseData?.town}, {caseData?.postcode}</p>
                    </div>
                    <div className="p-4 bg-slate-50 rounded-lg">
                      <p className="text-xs text-slate-500 uppercase mb-1">Property Type</p>
                      <p className="font-medium capitalize">{caseData?.property_type || 'Not specified'}</p>
                      {caseData?.bedrooms && <p>{caseData.bedrooms} Bedrooms</p>}
                      {caseData?.tenure && <p className="capitalize">{caseData.tenure}</p>}
                    </div>
                  </div>
                </div>

                {/* Financial */}
                <div>
                  <h4 className="font-semibold text-slate-700 mb-4 flex items-center gap-2">
                    <PoundSterling className="w-5 h-5" />
                    Financial
                  </h4>
                  <div className="grid md:grid-cols-3 gap-4">
                    <div className="p-4 bg-slate-50 rounded-lg text-center">
                      <p className="text-xs text-slate-500 uppercase">Asking Price</p>
                      <p className="text-xl font-bold text-slate-700 mt-1">
                        £{(caseData?.asking_price || 0).toLocaleString()}
                      </p>
                    </div>
                    <div className="p-4 bg-emerald-50 rounded-lg text-center">
                      <p className="text-xs text-emerald-600 uppercase">Agreed Sale Price</p>
                      <p className="text-xl font-bold text-emerald-700 mt-1">
                        £{(caseData?.offer_price || 0).toLocaleString()}
                      </p>
                    </div>
                    <div className="p-4 bg-slate-50 rounded-lg text-center">
                      <p className="text-xs text-slate-500 uppercase">Buyer Position</p>
                      <p className="text-lg font-bold text-slate-700 mt-1 capitalize">
                        {caseData?.buyer_position?.replace(/_/g, ' ') || 'Not specified'}
                      </p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </main>

      {/* Footer */}
      <footer className="bg-black text-white py-6 mt-12">
        <div className="max-w-6xl mx-auto px-4 text-center">
          <p className="text-slate-400 text-sm">
            © {new Date().getFullYear()} Goodacres Residential. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default SellerProgressionPage;
